import { Button } from '@root/components'
import { Switch } from '@root/components/ui/Primitives'
import {
  DEBT_COLLECTION_DELAY_MAX_DAYS,
  DEBT_COLLECTION_DELAY_MIN_COMPANY_DAYS,
  DEBT_COLLECTION_DELAY_MIN_PERSON_DAYS
} from '@constants'
import React from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FaExternalLinkAlt, FaFileSignature, FaInfoCircle } from 'react-icons/fa'
import styled from 'styled-components'
import {
  BadgeRow,
  CollectionBody,
  CollectionCard,
  CollectionDescription,
  CollectionHeader,
  CollectionHeaderCopy,
  CollectionTitle,
  DaysChip,
  DaysInput,
  DaysUnit,
  Eyebrow,
  FieldError,
  HandoverSwitch,
  InfoRow,
  LinkRow,
  MetaHint,
  OutlineBadge,
  ReminderMetaRow,
  SwitchLabelText
} from './PaymentRemindersAndCollectionForm.styled'
import { PaymentRemindersFormValues } from './paymentRemindersFormValues'

interface Props {
  kraviaVerified: boolean
  onActivate: () => void
}

export const CollectionHandoverCard: React.FC<Props> = ({ kraviaVerified, onActivate }) => {
  const [t] = useTranslation()
  const { control, register, formState } = useFormContext<PaymentRemindersFormValues>()
  const { errors } = formState
  const automaticCollection = useWatch({
    control,
    name: 'is_automatic_debt_collection_enabled'
  })

  if (!kraviaVerified) {
    return (
      <CollectionCard $dashed>
        <div>
          <BadgeRow>
            <Eyebrow $tone="collection">{t('settings.invoicing.debtCollectionEyebrow')}</Eyebrow>
            <OutlineBadge>{t('settings.invoicing.partnerKravia')}</OutlineBadge>
            <OutlineBadge $tone="warning">{t('settings.invoicing.setupNeeded')}</OutlineBadge>
          </BadgeRow>
          <CollectionTitle>{t('settings.invoicing.handoverTitle')}</CollectionTitle>
          <CollectionDescription>
            {t('settings.invoicing.handoverDescriptionSetup')}
          </CollectionDescription>
        </div>
        <LinkRow>
          <Button
            type="button"
            intent="primary"
            size="sm"
            icon={<FaFileSignature />}
            onClick={onActivate}
          >
            {t('settings.invoicing.activateCollection')}
          </Button>
          <PricingAndTermsButton />
        </LinkRow>
      </CollectionCard>
    )
  }

  return (
    <CollectionCard>
      <CollectionHeader>
        <CollectionHeaderCopy>
          <BadgeRow>
            <Eyebrow $tone="collection">{t('settings.invoicing.debtCollectionEyebrow')}</Eyebrow>
            <OutlineBadge>{t('settings.invoicing.partnerKravia')}</OutlineBadge>
          </BadgeRow>
          <CollectionTitle>{t('settings.invoicing.handoverTitle')}</CollectionTitle>
          <CollectionDescription>
            {t('settings.invoicing.handoverDescription')}
          </CollectionDescription>
          <Controller
            control={control}
            name="is_automatic_debt_collection_enabled"
            render={({ field }) => (
              <HandoverSwitch>
                <Switch
                  height={22}
                  checked={field.value}
                  onChange={e => field.onChange(e.target.checked)}
                  name={field.name}
                />
                <SwitchLabelText $on={field.value}>
                  {t('settings.invoicing.automaticCollectionSwitch')}
                </SwitchLabelText>
              </HandoverSwitch>
            )}
          />
        </CollectionHeaderCopy>
      </CollectionHeader>
      <CollectionBody>
        {automaticCollection ? (
          <>
            <ReminderMetaRow>
              <DaysChip>
                <DaysInput
                  type="number"
                  min={DEBT_COLLECTION_DELAY_MIN_COMPANY_DAYS}
                  max={DEBT_COLLECTION_DELAY_MAX_DAYS}
                  {...register('debt_collection_delay_days_company', {
                    valueAsNumber: true,
                    shouldUnregister: true,
                    min: {
                      value: DEBT_COLLECTION_DELAY_MIN_COMPANY_DAYS,
                      message: t('settings.invoicing.collectionDelayCompanyMin')
                    },
                    max: {
                      value: DEBT_COLLECTION_DELAY_MAX_DAYS,
                      message: t('settings.invoicing.collectionDelayMax')
                    }
                  })}
                />
                <DaysUnit>{t('settings.invoicing.daysUnit')}</DaysUnit>
              </DaysChip>
              <MetaHint>{t('settings.invoicing.collectionDelayCompanies')}</MetaHint>
              <DaysChip>
                <DaysInput
                  type="number"
                  min={DEBT_COLLECTION_DELAY_MIN_PERSON_DAYS}
                  max={DEBT_COLLECTION_DELAY_MAX_DAYS}
                  {...register('debt_collection_delay_days_person', {
                    valueAsNumber: true,
                    shouldUnregister: true,
                    min: {
                      value: DEBT_COLLECTION_DELAY_MIN_PERSON_DAYS,
                      message: t('settings.invoicing.collectionDelayPersonMin')
                    },
                    max: {
                      value: DEBT_COLLECTION_DELAY_MAX_DAYS,
                      message: t('settings.invoicing.collectionDelayMax')
                    }
                  })}
                />
                <DaysUnit>{t('settings.invoicing.daysUnit')}</DaysUnit>
              </DaysChip>
              <MetaHint>{t('settings.invoicing.collectionDelayConsumers')}</MetaHint>
            </ReminderMetaRow>
            {(errors.debt_collection_delay_days_company ||
              errors.debt_collection_delay_days_person) && (
              <FieldError>
                {errors.debt_collection_delay_days_company?.message ||
                  errors.debt_collection_delay_days_person?.message}
              </FieldError>
            )}
            <InfoRow>
              <FaInfoCircle />
              <span>{t('settings.invoicing.automaticDebtCollectionAlert')}</span>
            </InfoRow>
          </>
        ) : (
          <InfoRow>
            <FaInfoCircle />
            <span>{t('settings.invoicing.manualCollectionHint')}</span>
          </InfoRow>
        )}
        <LinkRow>
          <PricingAndTermsButton />
        </LinkRow>
      </CollectionBody>
    </CollectionCard>
  )
}

const PricingAndTermsButton: React.FC = () => {
  const [t] = useTranslation()
  const label = t('settings.invoicing.pricingAndTerms')
  return (
    <PricingAndTermsAnchor
      href={t('link.kraviaSupport')}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
    >
      <Button type="button" tabIndex={-1} isSecondary size="sm" iconRight={<FaExternalLinkAlt />}>
        {label}
      </Button>
    </PricingAndTermsAnchor>
  )
}

const PricingAndTermsAnchor = styled.a`
  display: inline-flex;
  text-decoration: none;
  color: inherit;
  font-size: inherit;
  font-weight: inherit;
  cursor: pointer;

  button {
    pointer-events: none;
  }
`
